import React, { useState } from "react";
import { useNode, useEditor } from "@craftjs/core";
import {
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
  IconButton,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon, ExpandMore } from "@mui/icons-material";
import { v4 as uuidv4 } from 'uuid';
import { CardCraft } from './CardCraft'; // Import CardCraft component

export const NavbarControl = ({
  name,
  hidden,
  style,
  brandEnabled = true, // New prop for brand enable/disable
  preBrandImage = "",
  preBrandImageWidth = "32px",
  preBrandImageHeight = "32px",
  brandText = "Brand",
  brandImage = "",
  brandDocumentId = "", // Add documentId for brand
  menuItems = [
    { id: uuidv4(), label: "Home", hasDropdown: false, dropdownItems: [], documentId: "" },
    { id: uuidv4(), label: "About", hasDropdown: false, dropdownItems: [], documentId: "" }
  ],
  endItems = [
    { id: uuidv4(), label: "Login", type: "button", documentId: "" }
  ],
  activeItem = "brand",
  isFixed = false,
  fixedPosition = "top",
  isTransparent = false,
  hasSpaced = false,
  hasShadow = false,
  color = "",
  children,
  ...props
}) => {
  const {
    connectors: { connect, drag },
    isSelected,
    isHovered,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (hidden) return null;

  const getSelectionStyle = () => {
    if (!enabled) return {};

    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  const handleItemClick = (itemId) => {
    setProp(props => props.activeItem = itemId);
  };

  const buildNavbarClasses = () => {
    const classes = ['navbar'];

    if (color) classes.push(color);
    if (isFixed) classes.push(`is-fixed-${fixedPosition}`);
    if (isTransparent) classes.push('is-transparent');
    if (hasSpaced) classes.push('is-spaced');
    if (hasShadow) classes.push('has-shadow');

    return classes.join(' ');
  };

  const renderDropdownItems = (parentItem) => {
    return parentItem.dropdownItems.map((dropdownItem) => (
      <a
        key={dropdownItem.id}
        className={`navbar-item ${activeItem === `dropdown-${dropdownItem.id}` ? 'is-active' : ''}`}
        onClick={() => handleItemClick(`dropdown-${dropdownItem.id}`)}
        style={{ cursor: 'pointer' }}
      >
        {dropdownItem.label}
      </a>
    ));
  };

  const renderActiveContent = () => {
    return (
      <div style={{ position: 'relative', height: '100%' }}>
        {/* Brand content - only render if brand is enabled */}
        {brandEnabled && (
          <div style={{ display: activeItem === 'brand' ? 'block' : 'none', height: '100%' }}>
            <CardCraft
              documentId={brandDocumentId}
              qwantumId="card"
              name={`${name} - Brand Content`}
              width="100%"
              height="100%"
            />
          </div>
        )}

        {/* Menu items content */}
        {menuItems.map((menuItem) => (
          <div
            key={`menu-content-${menuItem.id}`}
            style={{ display: activeItem === `menu-${menuItem.id}` ? 'block' : 'none', height: '100%' }}
          >
            <CardCraft
              documentId={menuItem.documentId}
              qwantumId="card"
              name={`${name} - ${menuItem.label}`}
              width="100%"
              height="100%"
            />
          </div>
        ))}

        {/* Dropdown items content */}
        {menuItems.map((menuItem) =>
          menuItem.dropdownItems.map((dropdownItem) => (
            <div
              key={`dropdown-content-${dropdownItem.id}`}
              style={{ display: activeItem === `dropdown-${dropdownItem.id}` ? 'block' : 'none', height: '100%' }}
            >
              <CardCraft
                documentId={dropdownItem.documentId}
                qwantumId="card"
                name={`${name} - ${dropdownItem.label}`}
                width="100%"
                height="100%"
              />
            </div>
          ))
        )}

        {/* End items content */}
        {endItems.map((endItem) => (
          <div
            key={`end-content-${endItem.id}`}
            style={{ display: activeItem === `end-${endItem.id}` ? 'block' : 'none', height: '100%' }}
          >
            <CardCraft
              documentId={endItem.documentId}
              qwantumId="card"
              name={`${name} - ${endItem.label}`}
              width="100%"
              height="100%"
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      name={name}
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      {/* Navbar */}
      <nav className={buildNavbarClasses()} style={{ 
        minHeight: brandEnabled && preBrandImage ? (preBrandImageHeight || '32px') : 'auto',
        height: 'auto',
        maxHeight: 'none'
      }}>
        {/* Brand - only render if enabled */}
        {brandEnabled && (
          <div className="navbar-brand" style={{ 
            minWidth: 'auto', 
            flexShrink: 0, 
            height: preBrandImage ? (preBrandImageHeight || '32px') : 'auto',
            maxHeight: 'none'
          }}>
            <a
              className={`navbar-item ${activeItem === 'brand' ? 'is-active' : ''}`}
              onClick={() => handleItemClick('brand')}
              style={{
                cursor: activeItem === 'brand' ? 'default' : 'pointer',
                pointerEvents: activeItem === 'brand' ? 'none' : 'auto',
                opacity: activeItem === 'brand' ? 0.6 : 1,
                backgroundColor: activeItem === 'brand' ? '#f5f5f5' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                paddingRight: brandText?.trim() ? '50px' : '12px',
                height: preBrandImage ? (preBrandImageHeight || '32px') : 'auto',
                minHeight: preBrandImage ? (preBrandImageHeight || '32px') : '3.25rem',
                maxHeight: 'none'
              }}
            >
              {/* Show pre-brand image only when brand text is empty */}
              {!brandText?.trim() && preBrandImage && (
                <img
                  src={preBrandImage}
                  alt="Pre Brand"
                  style={{
                    width: preBrandImageWidth || '32px',
                    height: preBrandImageHeight || '32px',
                    objectFit: 'contain',
                    display: 'block',
                    maxHeight: 'none',
                    minHeight: 'unset'
                  }}
                />
              )}
              
              {/* Show brand image and text when brand text exists */}
              {brandText?.trim() && (
                <>
                  {/* Pre-brand image */}
                  {preBrandImage && (
                    <img
                      src={preBrandImage}
                      alt="Pre Brand"
                      style={{
                        width: preBrandImageWidth || '32px',
                        height: preBrandImageHeight || '32px',
                        objectFit: 'contain',
                        marginRight: '0.5rem',
                        display: 'block',
                        maxHeight: 'none',
                        minHeight: 'unset'
                      }}
                    />
                  )}
                  
                  {/* Brand image */}
                  {brandImage && (
                    <img
                      src={brandImage}
                      alt="Brand"
                      style={{
                        maxHeight: '28px',
                        width: 'auto',
                        marginRight: '0.5rem'
                      }}
                    />
                  )}
                  
                  {/* Brand text */}
                  {brandText}
                </>
              )}
            </a>

            <div
              className={`navbar-burger burger ${mobileMenuOpen ? 'is-active' : ''}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        {/* Menu */}
        <div className={`navbar-menu ${mobileMenuOpen ? 'is-active' : ''}`}>
          <div className="navbar-start">
            {menuItems.map((item) => (
              item.hasDropdown ? (
                <div key={item.id} className="navbar-item has-dropdown is-hoverable">
                  <div
                    className={`navbar-link ${activeItem === `menu-${item.id}` ? 'is-active' : ''}`}
                    onClick={() => handleItemClick(`menu-${item.id}`)}
                    style={{
                      cursor: activeItem === `menu-${item.id}` ? 'default' : 'pointer',
                      pointerEvents: activeItem === `menu-${item.id}` ? 'none' : 'auto',
                      opacity: activeItem === `menu-${item.id}` ? 0.6 : 1,
                      backgroundColor: activeItem === `menu-${item.id}` ? '#f5f5f5' : 'transparent'
                    }}
                  >
                    {item.label}
                  </div>
                  <div className="navbar-dropdown">
                    {renderDropdownItems(item)}
                  </div>
                </div>
              ) : (
                <div
                  key={item.id}
                  className={`navbar-item ${activeItem === `menu-${item.id}` ? 'is-active' : ''}`}
                  onClick={() => handleItemClick(`menu-${item.id}`)}
                  style={{
                    cursor: activeItem === `menu-${item.id}` ? 'default' : 'pointer',
                    pointerEvents: activeItem === `menu-${item.id}` ? 'none' : 'auto',
                    opacity: activeItem === `menu-${item.id}` ? 0.6 : 1,
                    backgroundColor: activeItem === `menu-${item.id}` ? '#f5f5f5' : 'transparent'
                  }}
                >
                  {item.label}
                </div>
              )
            ))}
          </div>

          <div className="navbar-end">
            {endItems.map((item) => (
              <div
                key={item.id}
                className={`navbar-item ${activeItem === `end-${item.id}` ? 'is-active' : ''}`}
                onClick={() => handleItemClick(`end-${item.id}`)}
                style={{
                  cursor: activeItem === `end-${item.id}` ? 'default' : 'pointer',
                  pointerEvents: activeItem === `end-${item.id}` ? 'none' : 'auto',
                  opacity: activeItem === `end-${item.id}` ? 0.6 : 1,
                  backgroundColor: activeItem === `end-${item.id}` ? '#f5f5f5' : 'transparent'
                }}
              >
                {item.type === 'button' ? (
                  <div className="buttons">
                    <button className="button is-primary">
                      {item.label}
                    </button>
                  </div>
                ) : (
                  item.label
                )}
              </div>
            ))}
          </div>
        </div>
      </nav>

      {/* Active Content Area */}
      <div style={{ flex: 1, minHeight: '100px', position: 'relative' }}>
        {renderActiveContent()}
      </div>
    </div>
  );
};

const NavbarControlSettings = () => {
  const {
    name,
    brandEnabled,
    preBrandImage,
    preBrandImageWidth,
    preBrandImageHeight,
    brandText,
    brandImage,
    brandDocumentId,
    menuItems,
    endItems,
    activeItem,
    isFixed,
    fixedPosition,
    isTransparent,
    hasSpaced,
    hasShadow,
    color,
    hidden,
    actions: { setProp }
  } = useNode(node => ({
    name: node.data.props.name,
    brandEnabled: node.data.props.brandEnabled,
    preBrandImage: node.data.props.preBrandImage,
    preBrandImageWidth: node.data.props.preBrandImageWidth,
    preBrandImageHeight: node.data.props.preBrandImageHeight,
    brandText: node.data.props.brandText,
    brandImage: node.data.props.brandImage,
    brandDocumentId: node.data.props.brandDocumentId,
    menuItems: node.data.props.menuItems,
    endItems: node.data.props.endItems,
    activeItem: node.data.props.activeItem,
    isFixed: node.data.props.isFixed,
    fixedPosition: node.data.props.fixedPosition,
    isTransparent: node.data.props.isTransparent,
    hasSpaced: node.data.props.hasSpaced,
    hasShadow: node.data.props.hasShadow,
    color: node.data.props.color,
    hidden: node.data.props.hidden
  }));

  // Menu item functions
  const addMenuItem = () => {
    setProp(props => {
      props.menuItems = [...props.menuItems, {
        id: uuidv4(),
        label: `Item ${props.menuItems.length + 1}`,
        hasDropdown: false,
        dropdownItems: [],
        documentId: ""
      }];
    });
  };

  const removeMenuItem = (index) => {
    setProp(props => {
      if (props.menuItems.length > 0) {
        const removedItem = props.menuItems[index];
        props.menuItems = props.menuItems.filter((_, i) => i !== index);

        if (props.activeItem === `menu-${removedItem.id}`) {
          props.activeItem = props.brandEnabled ? 'brand' : (props.menuItems.length > 0 ? `menu-${props.menuItems[0].id}` : '');
        }
      }
    });
  };

  const updateMenuItem = (index, field, value) => {
    setProp(props => {
      props.menuItems[index][field] = value;
    });
  };

  const toggleDropdown = (index) => {
    setProp(props => {
      props.menuItems[index].hasDropdown = !props.menuItems[index].hasDropdown;
      if (!props.menuItems[index].hasDropdown) {
        props.menuItems[index].dropdownItems = [];
      }
    });
  };

  const addDropdownItem = (menuIndex) => {
    setProp(props => {
      props.menuItems[menuIndex].dropdownItems = [
        ...props.menuItems[menuIndex].dropdownItems,
        { 
          id: uuidv4(), 
          label: `Dropdown ${props.menuItems[menuIndex].dropdownItems.length + 1}`,
          documentId: ""
        }
      ];
    });
  };

  const removeDropdownItem = (menuIndex, dropdownIndex) => {
    setProp(props => {
      const removedItem = props.menuItems[menuIndex].dropdownItems[dropdownIndex];
      props.menuItems[menuIndex].dropdownItems = props.menuItems[menuIndex].dropdownItems.filter((_, i) => i !== dropdownIndex);

      if (props.activeItem === `dropdown-${removedItem.id}`) {
        props.activeItem = props.brandEnabled ? 'brand' : (props.menuItems.length > 0 ? `menu-${props.menuItems[0].id}` : '');
      }
    });
  };

  const updateDropdownItem = (menuIndex, dropdownIndex, field, value) => {
    setProp(props => {
      props.menuItems[menuIndex].dropdownItems[dropdownIndex][field] = value;
    });
  };

  // End item functions
  const addEndItem = () => {
    setProp(props => {
      props.endItems = [...props.endItems, {
        id: uuidv4(),
        label: `Button ${props.endItems.length + 1}`,
        type: 'button',
        documentId: ""
      }];
    });
  };

  const removeEndItem = (index) => {
    setProp(props => {
      if (props.endItems.length > 0) {
        const removedItem = props.endItems[index];
        props.endItems = props.endItems.filter((_, i) => i !== index);

        if (props.activeItem === `end-${removedItem.id}`) {
          props.activeItem = props.brandEnabled ? 'brand' : (props.menuItems.length > 0 ? `menu-${props.menuItems[0].id}` : '');
        }
      }
    });
  };

  const updateEndItem = (index, field, value) => {
    setProp(props => {
      props.endItems[index][field] = value;
    });
  };

  const setActiveItem = (itemId) => {
    setProp(props => props.activeItem = itemId);
  };

  const handleBrandToggle = (enabled) => {
    setProp(props => {
      props.brandEnabled = enabled;
      if (!enabled && props.activeItem === 'brand') {
        // Switch to first menu item if brand is disabled and was active
        props.activeItem = props.menuItems.length > 0 ? `menu-${props.menuItems[0].id}` : '';
      }
    });
  };

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={hidden || false}
            onChange={(e) => setProp(props => props.hidden = e.target.checked)}
          />
        }
        label="Hidden"
      />

      {/* Brand Settings */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Brand Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Checkbox
                checked={brandEnabled || false}
                onChange={(e) => handleBrandToggle(e.target.checked)}
              />
            }
            label="Enable Brand"
          />

          {brandEnabled && (
            <>
              <Button
                variant={activeItem === 'brand' ? "contained" : "outlined"}
                size="small"
                onClick={() => setActiveItem('brand')}
                fullWidth
                style={{ marginBottom: '10px', marginTop: '10px' }}
              >
                Edit Brand Content
              </Button>

              <FormControl fullWidth margin="normal">
                <FormLabel>Brand Document ID</FormLabel>
                <TextField
                  value={brandDocumentId || ''}
                  onChange={(e) => setProp(props => props.brandDocumentId = e.target.value)}
                  size="small"
                  placeholder="Enter document ID for brand content"
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>Pre-Brand Image URL</FormLabel>
                <TextField
                  value={preBrandImage || ''}
                  onChange={(e) => setProp(props => props.preBrandImage = e.target.value)}
                  size="small"
                  placeholder="https://example.com/icon.png"
                />
              </FormControl>

              <Box display="flex" gap={1}>
                <FormControl fullWidth margin="normal">
                  <FormLabel>Pre-Brand Width</FormLabel>
                  <TextField
                    value={preBrandImageWidth || '32px'}
                    onChange={(e) => setProp(props => props.preBrandImageWidth = e.target.value)}
                    size="small"
                    placeholder="32px"
                  />
                </FormControl>

                <FormControl fullWidth margin="normal">
                  <FormLabel>Pre-Brand Height</FormLabel>
                  <TextField
                    value={preBrandImageHeight || '32px'}
                    onChange={(e) => setProp(props => props.preBrandImageHeight = e.target.value)}
                    size="small"
                    placeholder="32px"
                  />
                </FormControl>
              </Box>

              <FormControl fullWidth margin="normal">
                <FormLabel>Brand Text</FormLabel>
                <TextField
                  value={brandText || ''}
                  onChange={(e) => setProp(props => props.brandText = e.target.value || null)}
                  size="small"
                  placeholder="Brand name"
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <FormLabel>Brand Image URL</FormLabel>
                <TextField
                  value={brandImage || ''}
                  onChange={(e) => setProp(props => props.brandImage = e.target.value)}
                  size="small"
                  placeholder="https://example.com/logo.png"
                />
              </FormControl>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Menu Items */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Menu Items</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {menuItems?.map((item, index) => (
            <Box key={item.id} marginBottom={2} padding={1} border="1px solid #ddd" borderRadius="4px">
              <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
                <Button
                  variant={activeItem === `menu-${item.id}` ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setActiveItem(`menu-${item.id}`)}
                  style={{ minWidth: '60px' }}
                >
                  {index + 1}
                </Button>
                <TextField
                  size="small"
                  placeholder="Menu Label"
                  value={item.label}
                  onChange={(e) => updateMenuItem(index, 'label', e.target.value)}
                  style={{ flex: 1 }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.hasDropdown}
                      onChange={() => toggleDropdown(index)}
                    />
                  }
                  label="Dropdown"
                />
                <IconButton
                  size="small"
                  onClick={() => removeMenuItem(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <FormControl fullWidth margin="normal">
                <FormLabel>Document ID</FormLabel>
                <TextField
                  size="small"
                  placeholder="Enter document ID"
                  value={item.documentId || ''}
                  onChange={(e) => updateMenuItem(index, 'documentId', e.target.value)}
                />
              </FormControl>

              {/* Dropdown Items */}
              {item.hasDropdown && (
                <Box marginLeft={2}>
                  <Typography variant="caption" style={{ fontWeight: 'bold' }}>Dropdown Items:</Typography>
                  {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                    <Box key={dropdownItem.id} marginTop={1}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Button
                          variant={activeItem === `dropdown-${dropdownItem.id}` ? "contained" : "outlined"}
                          size="small"
                          onClick={() => setActiveItem(`dropdown-${dropdownItem.id}`)}
                          style={{ minWidth: '40px' }}
                        >
                          {dropdownIndex + 1}
                        </Button>
                        <TextField
                          size="small"
                          placeholder="Dropdown Label"
                          value={dropdownItem.label}
                          onChange={(e) => updateDropdownItem(index, dropdownIndex, 'label', e.target.value)}
                          style={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeDropdownItem(index, dropdownIndex)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <FormControl fullWidth margin="normal">
                        <FormLabel>Document ID</FormLabel>
                        <TextField
                          size="small"
                          placeholder="Enter document ID"
                          value={dropdownItem.documentId || ''}
                          onChange={(e) => updateDropdownItem(index, dropdownIndex, 'documentId', e.target.value)}
                        />
                      </FormControl>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => addDropdownItem(index)}
                    size="small"
                    variant="text"
                    style={{ marginTop: '8px' }}
                  >
                    Add Dropdown Item
                  </Button>
                </Box>
              )}
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={addMenuItem}
            size="small"
            variant="outlined"
            fullWidth
          >
            Add Menu Item
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* End Items */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">End Items (Right Side)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {endItems?.map((item, index) => (
            <Box key={item.id} marginBottom={2} padding={1} border="1px solid #ddd" borderRadius="4px">
              <Box display="flex" alignItems="center" gap={1} marginBottom={1}>
                <Button
                  variant={activeItem === `end-${item.id}` ? "contained" : "outlined"}
                  size="small"
                  onClick={() => setActiveItem(`end-${item.id}`)}
                  style={{ minWidth: '60px' }}
                >
                  {index + 1}
                </Button>
                <TextField
                  size="small"
                  placeholder="Item Label"
                  value={item.label}
                  onChange={(e) => updateEndItem(index, 'label', e.target.value)}
                  style={{ flex: 1 }}
                />
                <Select
                  value={item.type || 'button'}
                  onChange={(e) => updateEndItem(index, 'type', e.target.value)}
                  size="small"
                  style={{ minWidth: '100px' }}
                >
                  <MenuItem value="button">Button</MenuItem>
                  <MenuItem value="link">Link</MenuItem>
                </Select>
                <IconButton
                  size="small"
                  onClick={() => removeEndItem(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <FormControl fullWidth margin="normal">
                <FormLabel>Document ID</FormLabel>
                <TextField
                  size="small"
                  placeholder="Enter document ID"
                  value={item.documentId || ''}
                  onChange={(e) => updateEndItem(index, 'documentId', e.target.value)}
                />
              </FormControl>
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={addEndItem}
            size="small"
            variant="outlined"
            fullWidth
          >
            Add End Item
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Navbar Styling */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle2">Navbar Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Color</FormLabel>
            <Select
              value={color || ''}
              onChange={(e) => setProp(props => props.color = e.target.value)}
              size="small"
            >
              <MenuItem value="">Default</MenuItem>
              <MenuItem value="is-white">White</MenuItem>
              <MenuItem value="is-black">Black</MenuItem>
              <MenuItem value="is-light">Light</MenuItem>
              <MenuItem value="is-dark">Dark</MenuItem>
              <MenuItem value="is-primary">Primary</MenuItem>
              <MenuItem value="is-link">Link</MenuItem>
              <MenuItem value="is-info">Info</MenuItem>
              <MenuItem value="is-success">Success</MenuItem>
              <MenuItem value="is-warning">Warning</MenuItem>
              <MenuItem value="is-danger">Danger</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={isFixed}
                onChange={(e) => setProp(props => props.isFixed = e.target.checked)}
              />
            }
            label="Fixed Position"
          />

          {isFixed && (
            <FormControl fullWidth margin="normal">
              <FormLabel>Fixed Position</FormLabel>
              <Select
                value={fixedPosition || 'top'}
                onChange={(e) => setProp(props => props.fixedPosition = e.target.value)}
                size="small"
              >
                <MenuItem value="top">Top</MenuItem>
                <MenuItem value="bottom">Bottom</MenuItem>
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={isTransparent}
                onChange={(e) => setProp(props => props.isTransparent = e.target.checked)}
              />
            }
            label="Transparent"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={hasSpaced}
                onChange={(e) => setProp(props => props.hasSpaced = e.target.checked)}
              />
            }
            label="Spaced"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={hasShadow}
                onChange={(e) => setProp(props => props.hasShadow = e.target.checked)}
              />
            }
            label="Shadow"
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export const NavbarControlDefaultProps = {
  name: "NavbarControl",
  brandEnabled: true,
  preBrandImage: "",
  preBrandImageWidth: "32px", 
  preBrandImageHeight: "32px",
  brandText: "Brand",
  brandImage: "",
  brandDocumentId: "",
  menuItems: [
    { id: uuidv4(), label: "Home", hasDropdown: false, dropdownItems: [], documentId: "" },
    { id: uuidv4(), label: "About", hasDropdown: false, dropdownItems: [], documentId: "" }
  ],
  endItems: [
    { id: uuidv4(), label: "Login", type: "button", documentId: "" }
  ],
  activeItem: "brand",
  isFixed: false,
  fixedPosition: "top",
  isTransparent: false,
  hasSpaced: false,
  hasShadow: false,
  color: "",
  hidden: false
};

NavbarControl.craft = {
  props: NavbarControlDefaultProps,
  related: {
    settings: NavbarControlSettings
  },
  rules: {
    canDrag: () => true,
    canDrop: () => true,
    canMoveIn: () => true
  },
  displayName: 'Navbar Control'
};